import { BlankContent, Spinner } from '@cint/wonderment';
import { useEffect, useMemo } from 'react';

import { useFetchData } from '@/api/apiHooks';
import { PanelDropDownItem } from '@/utils/types';
import { URLS } from '@/utils/urls';

import { ReportFiltersSheet } from './ReportFiltersSheet';

interface DefaultReportFiltersProps {
  selectedPanel?: string;
  filters: Record<string, any>;
  onFiltersChange: (
    updatedFilters: Partial<DefaultReportFiltersProps['filters']>
  ) => void;
  selectedCompany: string;
  onPanelCreatedDateChange?: (date: Date | null) => void;
}

export const PanelistInsightsReportFilters = ({
  filters,
  selectedPanel,
  onFiltersChange,
  selectedCompany,
  onPanelCreatedDateChange,
}: DefaultReportFiltersProps) => {
  const {
    data: panelsData,
    error: panelsError,
    isLoading: isPanelsLoading,
  } = useFetchData<PanelDropDownItem[]>({
    url: URLS.getAllCompanyPanels(selectedCompany),
    enabled: !!selectedCompany,
    staleTime: 10 * 60 * 60 * 1000,
  });

  const panelsOptions = useMemo(() => {
    if (panelsData?.length) {
      return panelsData.map((panel) => ({
        label: panel.value,
        id: panel.key,
        created_at: panel.additional_info?.created_at
          ? new Date(panel.additional_info.created_at)
          : null,
      }));
    }
    return [];
  }, [panelsData]);

  const selectedPanelCreatedDate = useMemo(() => {
    if (!selectedPanel || !panelsData?.length) return null;

    const selectedPanelData = panelsData.find(
      (panel) => panel.key === selectedPanel
    );

    if (selectedPanelData?.additional_info?.created_at) {
      return new Date(selectedPanelData.additional_info.created_at);
    }

    return null;
  }, [panelsData, selectedPanel]);

  useEffect(() => {
    if (onPanelCreatedDateChange) {
      onPanelCreatedDateChange(selectedPanelCreatedDate);
    }
  }, [selectedPanelCreatedDate, onPanelCreatedDateChange]);

  useEffect(() => {
    if (!selectedPanelCreatedDate) return;

    const fromDate = filters.selectedDates?.from
      ? new Date(filters.selectedDates.from)
      : null;

    if (!fromDate || fromDate < selectedPanelCreatedDate) {
      onFiltersChange({
        selectedDates: {
          from: selectedPanelCreatedDate,
          to: filters.selectedDates?.to,
        },
      });
    }
  }, [selectedPanelCreatedDate, onFiltersChange, filters.selectedDates]);

  useEffect(() => {
    if (
      selectedPanel &&
      (!filters.selectedPanelsForComparison ||
        !filters.selectedPanelsForComparison?.includes(selectedPanel))
    ) {
      onFiltersChange({
        selectedPanelsForComparison: [selectedPanel],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPanel]);

  if (panelsError) {
    return (
      <BlankContent
        description="Please try again later"
        size="sm"
        title="Something went wrong"
      />
    );
  }

  if (isPanelsLoading) {
    return <Spinner />;
  }

  return (
    <ReportFiltersSheet
      filters={filters}
      onFiltersChange={onFiltersChange}
      // even when cleared we want default filter to stay selected
      clearedFilterState={[selectedPanel]}
      selectedPanel={selectedPanel}
      filterOptions={panelsOptions}
      filterKey="selectedPanelsForComparison"
      filterBy="panel"
      minDate={selectedPanelCreatedDate}
    />
  );
};
